import { useCallback, useMemo, useState } from 'react';
import { Card } from '@safetyhub/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getSiteProcessTypes,
  createSiteProcessType,
  updateSiteProcessType,
  deleteSiteProcessType,
  reorderSiteProcessTypes,
  type SiteProcessType,
} from '../../../../lib/site-metadata';
import { GripVerticalIcon } from './Icons';
import { ProcessTypeFormModal } from './ProcessTypeFormModal';
import { ProcessTypeDeleteModal } from './ProcessTypeDeleteModal';

interface Props {
  companyId: string;
  siteId: string;
  isOwner: boolean;
}

// 단일 항목 (Sortable)
interface SortableItemProps {
  item: SiteProcessType;
  isOwner: boolean;
  level: number;
  subtitle?: string;
  onEdit: (item: SiteProcessType) => void;
  onDelete: (item: SiteProcessType) => void;
  onAddChild?: (parentId: string) => void;
  children?: React.ReactNode;
}

function SortableItem({ item, isOwner, level, subtitle, onEdit, onDelete, onAddChild, children }: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="min-w-0">
      <div
        className={`flex items-center py-3 gap-2 group min-w-0 ${level === 1 ? 'pl-6' : ''}`}
      >
        {isOwner && (
          <span
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-ink-300 hover:text-ink-500 flex-shrink-0 px-1 touch-none select-none"
            aria-label="순서 변경"
          >
            <GripVerticalIcon className="w-4 h-4" />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink-900 truncate">{item.name}</p>
          {subtitle && <p className="text-xs text-ink-500 truncate">{subtitle}</p>}
        </div>
        {isOwner && (
          <div className="hidden group-hover:flex items-center gap-1 flex-shrink-0">
            {level === 0 && onAddChild && (
              <button
                onClick={() => onAddChild(item.id)}
                className="text-xs text-brand-600 hover:text-brand-800 px-1.5 py-0.5 rounded hover:bg-brand-50"
              >
                + 하위
              </button>
            )}
            <button
              onClick={() => onEdit(item)}
              className="text-xs text-ink-500 hover:text-ink-700 px-1.5 py-0.5 rounded hover:bg-surface-hover"
            >
              수정
            </button>
            <button
              onClick={() => onDelete(item)}
              className="text-xs text-red-500 hover:text-red-700 px-1.5 py-0.5 rounded hover:bg-red-50"
            >
              삭제
            </button>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// 모달 mode union — 9개 useState → 단일 식별자
type ModalMode =
  | { type: 'edit'; item: SiteProcessType }
  | { type: 'create'; parentId: string | null }
  | null;

export function SiteProcessTypesSection({ companyId, siteId, isOwner }: Props) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['site-process-types', companyId, siteId] as const, [companyId, siteId]);

  const { data: items = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => getSiteProcessTypes(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });

  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [deleteTarget, setDeleteTarget] = useState<SiteProcessType | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // O(N) 한 번 순회로 topLevel + childrenByParent Map 캐싱
  const { topLevel, childrenByParent } = useMemo(() => {
    const top: SiteProcessType[] = [];
    const map = new Map<string, SiteProcessType[]>();
    for (const item of items) {
      if (!item.isActive) continue;
      if (item.parentId === null) {
        top.push(item);
      } else {
        const arr = map.get(item.parentId) ?? [];
        arr.push(item);
        map.set(item.parentId, arr);
      }
    }
    top.sort((a, b) => a.order - b.order);
    map.forEach((arr) => arr.sort((a, b) => a.order - b.order));
    return { topLevel: top, childrenByParent: map };
  }, [items]);

  const reorderMutation = useMutation({
    mutationFn: ({ parentId, orderedIds }: { parentId: string | null; orderedIds: string[] }) =>
      reorderSiteProcessTypes(companyId, siteId, { parentId, orderedIds }),
    onMutate: async ({ parentId, orderedIds }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SiteProcessType[]>(queryKey);
      const orderMap = new Map(orderedIds.map((id, idx) => [id, idx]));
      queryClient.setQueryData<SiteProcessType[]>(queryKey, (old) =>
        old?.map((item) =>
          item.parentId === parentId && orderMap.has(item.id)
            ? { ...item, order: orderMap.get(item.id)! }
            : item,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const handleDragEnd = useCallback(
    (parentId: string | null) => (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const list = parentId === null ? topLevel : childrenByParent.get(parentId) ?? [];
      const oldIndex = list.findIndex((i) => i.id === active.id);
      const newIndex = list.findIndex((i) => i.id === over.id);
      // active 또는 over가 이 그룹에 속하지 않으면 silent drop (외부 DnD 컨텍스트로 누수 방지)
      if (oldIndex < 0 || newIndex < 0) return;
      const orderedIds = arrayMove(list, oldIndex, newIndex).map((i) => i.id);
      reorderMutation.mutate({ parentId, orderedIds });
    },
    [topLevel, childrenByParent, reorderMutation],
  );

  const openAddTop = useCallback(() => setModalMode({ type: 'create', parentId: null }), []);
  const openAddChild = useCallback((parentId: string) => setModalMode({ type: 'create', parentId }), []);
  const openEdit = useCallback((item: SiteProcessType) => setModalMode({ type: 'edit', item }), []);
  const closeModal = useCallback(() => setModalMode(null), []);

  const handleSubmitForm = useCallback(
    async (name: string) => {
      if (!modalMode) return;
      if (modalMode.type === 'edit') {
        await updateSiteProcessType(companyId, siteId, modalMode.item.id, { name });
      } else {
        await createSiteProcessType(companyId, siteId, { name, parentId: modalMode.parentId });
      }
      queryClient.invalidateQueries({ queryKey });
      setModalMode(null);
    },
    [modalMode, companyId, siteId, queryClient, queryKey],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteSiteProcessType(companyId, siteId, deleteTarget.id);
    queryClient.invalidateQueries({ queryKey });
    setDeleteTarget(null);
  }, [deleteTarget, companyId, siteId, queryClient, queryKey]);

  const modalTitle = !modalMode
    ? ''
    : modalMode.type === 'edit'
      ? '공종/공정 수정'
      : modalMode.parentId === null
        ? '새 공종 추가'
        : '하위 공정 추가';

  // 여백이 화면폭별로 갈려(p-4 sm:p-6) padding prop을 끄고 className으로 준다 — Card 주석의 탈출구
  return (
    <Card padding="none" radius="lg" elevation="md" className="p-4 sm:p-6 min-w-0">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-medium text-ink-900">공종/공정</h2>
        {isOwner && (
          <button onClick={openAddTop} className="text-sm text-brand-600 hover:text-brand-800">
            + 추가
          </button>
        )}
      </div>
      <p className="text-xs text-ink-400 mb-4">현장 내 공종/공정 목록. 대상(설비) 및 사진 분류에 사용됩니다.</p>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : topLevel.length === 0 ? (
        <div className="text-center py-6">
          <svg className="mx-auto h-8 w-8 text-ink-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="mt-2 text-sm text-ink-500">등록된 공종/공정이 없습니다.</p>
          {isOwner && (
            <button onClick={openAddTop} className="mt-2 text-sm text-brand-600 hover:text-brand-800">
              공종/공정 추가하기
            </button>
          )}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd(null)}>
          <SortableContext items={topLevel.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="divide-y divide-line">
              {topLevel.map((parent) => {
                const children = childrenByParent.get(parent.id) ?? [];
                const parentSubtitle = children.length > 0 ? `하위 ${children.length}개` : '세부 공정 없음';
                return (
                  <SortableItem
                    key={parent.id}
                    item={parent}
                    isOwner={isOwner}
                    level={0}
                    subtitle={parentSubtitle}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                    onAddChild={openAddChild}
                  >
                    {children.length > 0 && (
                      <ChildSortableGroup
                        parentId={parent.id}
                        items={children}
                        sensors={sensors}
                        isOwner={isOwner}
                        onEdit={openEdit}
                        onDelete={setDeleteTarget}
                        onDragEnd={handleDragEnd(parent.id)}
                      />
                    )}
                  </SortableItem>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {modalMode && (
        <ProcessTypeFormModal
          key={modalMode.type === 'edit' ? modalMode.item.id : `create-${modalMode.parentId ?? 'root'}`}
          title={modalTitle}
          isEdit={modalMode.type === 'edit'}
          initialName={modalMode.type === 'edit' ? modalMode.item.name : ''}
          onSubmit={handleSubmitForm}
          onClose={closeModal}
        />
      )}

      {deleteTarget && (
        <ProcessTypeDeleteModal
          itemName={deleteTarget.name}
          hasChildren={
            deleteTarget.parentId === null && (childrenByParent.get(deleteTarget.id)?.length ?? 0) > 0
          }
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </Card>
  );
}

interface ChildSortableGroupProps {
  parentId: string;
  items: SiteProcessType[];
  sensors: ReturnType<typeof useSensors>;
  isOwner: boolean;
  onEdit: (item: SiteProcessType) => void;
  onDelete: (item: SiteProcessType) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

function ChildSortableGroup({ items, sensors, isOwner, onEdit, onDelete, onDragEnd }: ChildSortableGroupProps) {
  const childIds = useMemo(() => items.map((c) => c.id), [items]);
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
        <div className="border-l border-line ml-6 mb-1 min-w-0">
          {items.map((child) => (
            <SortableItem
              key={child.id}
              item={child}
              isOwner={isOwner}
              level={1}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
